import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSpreadsheet,
  Scale,
  TrendingUp,
  TrendingDown,
  Loader2,
  Download,
  Eye,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  BarChart3,
  Percent,
  Hash,
} from "lucide-react";
import { financialStatementsApi } from "../../services/api";
import AccountLedgerModal from "./AccountLedgerModal";
import PDFPreviewModal from "./PDFPreviewModal";
import { PeriodKey, periodOptions, getDateRange } from "./periodPresets";

type SubTab = "trial-balance" | "balance-sheet" | "income-statement";

const subTabs = [
  { key: "trial-balance" as const, label: "Trial Balance", Icon: FileSpreadsheet },
  { key: "balance-sheet" as const, label: "Balance Sheet", Icon: Scale },
  { key: "income-statement" as const, label: "Income Statement", Icon: TrendingUp },
];

const typeColors: Record<string, { bg: string; text: string }> = {
  asset: { bg: "bg-blue-50", text: "text-blue-700" },
  liability: { bg: "bg-amber-50", text: "text-amber-700" },
  equity: { bg: "bg-purple-50", text: "text-purple-700" },
  revenue: { bg: "bg-emerald-50", text: "text-emerald-700" },
  expense: { bg: "bg-red-50", text: "text-red-700" },
};

const sectionAccent: Record<string, string> = {
  asset: "border-l-blue-500",
  liability: "border-l-amber-500",
  equity: "border-l-purple-500",
  revenue: "border-l-emerald-500",
  expense: "border-l-red-500",
};

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function startOfYear(): string {
  const year = new Date().getFullYear();
  return `${year}-01-01`;
}

function formatDateDisplay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

interface LedgerTarget {
  accountId: string;
  code: string;
  name: string;
  type: string;
}

interface PDFPreview {
  fetchFn: () => Promise<any>;
  title: string;
  subtitle: string;
  filename: string;
}

// ─── Summary Card ────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  label: string;
  value: string;
  Icon: React.FC<any>;
  color: string;
  bg: string;
  index: number;
  subtitle?: string;
}> = ({ label, value, Icon, color, bg, index, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
  >
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
        {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className={`p-2.5 rounded-xl ${bg} flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
    </div>
  </motion.div>
);

// ─── Main Component ──────────────────────────────────────────────────

const FinancialStatementsTab: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>("trial-balance");
  const [period, setPeriod] = useState<PeriodKey>("this-year");
  const [dateFrom, setDateFrom] = useState(startOfYear());
  const [dateTo, setDateTo] = useState(todayISO());
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<false | "csv">(false);
  const [data, setData] = useState<any>(null);
  const [ledgerTarget, setLedgerTarget] = useState<LedgerTarget | null>(null);
  const [pdfPreview, setPdfPreview] = useState<PDFPreview | null>(null);

  const handlePeriodChange = (key: PeriodKey) => {
    setPeriod(key);
    if (key === "custom") return;
    const range = getDateRange(key);
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
      setAsOfDate(range.to);
    }
  };

  const loadStatement = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      let res;
      if (subTab === "trial-balance") {
        res = await financialStatementsApi.trialBalance({ dateFrom, dateTo });
      } else if (subTab === "balance-sheet") {
        res = await financialStatementsApi.balanceSheet({ asOfDate });
      } else {
        res = await financialStatementsApi.incomeStatement({ dateFrom, dateTo });
      }
      setData(res.data);
    } catch (err) {
      console.error("Failed to load financial statement:", err);
    } finally {
      setLoading(false);
    }
  }, [subTab, dateFrom, dateTo, asOfDate]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const handleCSVExport = async () => {
    setExporting("csv");
    try {
      let res;
      if (subTab === "trial-balance") {
        res = await financialStatementsApi.exportTrialBalance({ dateFrom, dateTo });
      } else if (subTab === "balance-sheet") {
        res = await financialStatementsApi.exportBalanceSheet({ asOfDate });
      } else {
        res = await financialStatementsApi.exportIncomeStatement({ dateFrom, dateTo });
      }
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${subTab}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const handlePDFPreview = () => {
    const statementLabel = subTab === "trial-balance" ? "Trial Balance"
      : subTab === "balance-sheet" ? "Balance Sheet"
      : "Income Statement";

    const subtitle = subTab === "balance-sheet"
      ? `As of ${formatDateDisplay(asOfDate)}`
      : `${formatDateDisplay(dateFrom)} — ${formatDateDisplay(dateTo)}`;

    let fetchFn: () => Promise<any>;
    if (subTab === "trial-balance") {
      fetchFn = () => financialStatementsApi.exportTrialBalancePDF({ dateFrom, dateTo });
    } else if (subTab === "balance-sheet") {
      fetchFn = () => financialStatementsApi.exportBalanceSheetPDF({ asOfDate });
    } else {
      fetchFn = () => financialStatementsApi.exportIncomeStatementPDF({ dateFrom, dateTo });
    }

    setPdfPreview({
      fetchFn,
      title: statementLabel,
      subtitle,
      filename: `${subTab}.pdf`,
    });
  };

  // ─── Summary Cards Data ──────────────────────────────────────────

  const renderSummaryCards = () => {
    if (!data) return null;

    if (subTab === "trial-balance") {
      const totals = data.totals || {};
      const accountCount = (data.accounts || []).length;
      const isBalanced = totals.totalDebitCents === totals.totalCreditCents;
      return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total Debits" value={formatCents(totals.totalDebitCents || 0)} Icon={TrendingUp} color="text-blue-700" bg="bg-blue-50" index={0} />
          <SummaryCard label="Total Credits" value={formatCents(totals.totalCreditCents || 0)} Icon={TrendingDown} color="text-emerald-700" bg="bg-emerald-50" index={1} />
          <SummaryCard label="Accounts" value={String(accountCount)} Icon={Hash} color="text-slate-700" bg="bg-slate-100" index={2} />
          <SummaryCard
            label="Status"
            value={isBalanced ? "Balanced" : "Out of Balance"}
            Icon={isBalanced ? CheckCircle2 : AlertTriangle}
            color={isBalanced ? "text-emerald-700" : "text-red-600"}
            bg={isBalanced ? "bg-emerald-50" : "bg-red-50"}
            index={3}
          />
        </div>
      );
    }

    if (subTab === "balance-sheet") {
      const isBalanced = data.isBalanced !== false;
      return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total Assets" value={formatCents(data.assets?.totalCents || 0)} Icon={DollarSign} color="text-blue-700" bg="bg-blue-50" index={0} />
          <SummaryCard label="Total Liabilities" value={formatCents(data.liabilities?.totalCents || 0)} Icon={BarChart3} color="text-amber-700" bg="bg-amber-50" index={1} />
          <SummaryCard label="Total Equity" value={formatCents(data.equity?.totalCents || 0)} Icon={Scale} color="text-purple-700" bg="bg-purple-50" index={2} />
          <SummaryCard
            label="Status"
            value={isBalanced ? "Balanced" : "Out of Balance"}
            Icon={isBalanced ? CheckCircle2 : AlertTriangle}
            color={isBalanced ? "text-emerald-700" : "text-red-600"}
            bg={isBalanced ? "bg-emerald-50" : "bg-red-50"}
            index={3}
          />
        </div>
      );
    }

    // Income Statement
    const revenueCents = data.revenue?.totalCents || 0;
    const expenseCents = data.expenses?.totalCents || 0;
    const netIncome = data.netIncomeCents || 0;
    const margin = revenueCents > 0 ? ((netIncome / revenueCents) * 100).toFixed(1) : "0.0";

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Revenue" value={formatCents(revenueCents)} Icon={TrendingUp} color="text-emerald-700" bg="bg-emerald-50" index={0} />
        <SummaryCard label="Total Expenses" value={formatCents(expenseCents)} Icon={TrendingDown} color="text-red-600" bg="bg-red-50" index={1} />
        <SummaryCard
          label="Net Income"
          value={formatCents(netIncome)}
          Icon={DollarSign}
          color={netIncome >= 0 ? "text-emerald-700" : "text-red-600"}
          bg={netIncome >= 0 ? "bg-emerald-50" : "bg-red-50"}
          index={2}
        />
        <SummaryCard label="Profit Margin" value={`${margin}%`} Icon={Percent} color="text-blue-700" bg="bg-blue-50" index={3} />
      </div>
    );
  };

  // ─── Date Subtitle ───────────────────────────────────────────────

  const getDateSubtitle = () => {
    if (subTab === "balance-sheet") {
      return `As of ${formatDateDisplay(data?.asOfDate || asOfDate)}`;
    }
    if (data?.dateFrom && data?.dateTo) {
      return `${formatDateDisplay(data.dateFrom)} — ${formatDateDisplay(data.dateTo)}`;
    }
    return "All time";
  };

  const getStatementTitle = () => {
    if (subTab === "trial-balance") return "Trial Balance";
    if (subTab === "balance-sheet") return "Balance Sheet";
    return "Income Statement";
  };

  return (
    <div>
      {/* Animated Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 mb-6 inline-flex gap-1">
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className="relative flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            style={{ color: subTab === tab.key ? "#ffffff" : "#64748b" }}
          >
            {subTab === tab.key && (
              <motion.div
                layoutId="financial-statement-tab"
                className="absolute inset-0 rounded-lg"
                style={{ backgroundColor: "var(--color-primary)" }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              <tab.Icon className="h-4 w-4" />
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Period presets + date filters + export */}
      <div className="mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {periodOptions.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                period === p.key
                  ? "bg-brand text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {subTab === "balance-sheet" ? (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">As of Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => { setAsOfDate(e.target.value); setPeriod("custom"); }}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPeriod("custom"); }}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPeriod("custom"); }}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleCSVExport}
              disabled={!!exporting || loading || !data}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              {exporting === "csv" ? "Exporting..." : "CSV"}
            </button>
            <button
              onClick={handlePDFPreview}
              disabled={loading || !data}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-colors bg-brand hover:bg-brand-hover"
            >
              <Eye className="h-4 w-4" />
              Preview PDF
            </button>
          </div>
        </div>
      </div>

      {/* Statement content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : !data ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">No data available</p>
          <p className="text-sm text-slate-400 mt-1">
            Journal entries are needed to generate financial statements
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {renderSummaryCards()}

          {/* Report Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            {/* Premium Report Header */}
            <div className="bg-gradient-to-r from-brand to-brand-hover px-6 py-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">{data.firmName}</h2>
                  <p className="text-sm font-medium text-emerald-100 tracking-wide uppercase mt-1">
                    {getStatementTitle()}
                  </p>
                  <p className="text-xs text-emerald-200/70 mt-1">{getDateSubtitle()}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-emerald-200/60 uppercase tracking-wider">Generated</p>
                  <p className="text-xs text-emerald-100/80 mt-0.5">
                    {new Date().toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              </div>
            </div>

            {/* Table Content */}
            {subTab === "trial-balance" && <TrialBalanceTable data={data} onDrillDown={setLedgerTarget} />}
            {subTab === "balance-sheet" && <BalanceSheetTable data={data} onDrillDown={setLedgerTarget} />}
            {subTab === "income-statement" && <IncomeStatementTable data={data} onDrillDown={setLedgerTarget} />}
          </motion.div>
        </>
      )}

      {/* Account Ledger Drill-Down */}
      <AccountLedgerModal
        open={!!ledgerTarget}
        onClose={() => setLedgerTarget(null)}
        accountId={ledgerTarget?.accountId || ""}
        accountCode={ledgerTarget?.code || ""}
        accountName={ledgerTarget?.name || ""}
        accountType={ledgerTarget?.type}
      />

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <PDFPreviewModal
          open={!!pdfPreview}
          onClose={() => setPdfPreview(null)}
          title={pdfPreview.title}
          subtitle={pdfPreview.subtitle}
          fetchPDF={pdfPreview.fetchFn}
          filename={pdfPreview.filename}
        />
      )}
    </div>
  );
};

// ─── Trial Balance ──────────────────────────────────────────────────

const TrialBalanceTable: React.FC<{ data: any; onDrillDown: (t: LedgerTarget) => void }> = ({ data, onDrillDown }) => {
  const accounts = data.accounts || [];
  const totals = data.totals || {};

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand">
            <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Code</th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Account Name</th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Type</th>
            <th className="text-right px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Debit</th>
            <th className="text-right px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Credit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {accounts.map((acct: any, i: number) => {
            const colors = typeColors[acct.type] || { bg: "bg-slate-100", text: "text-slate-600" };
            return (
              <tr
                key={acct.accountId}
                className={`hover:bg-slate-50 cursor-pointer transition-colors ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
                onClick={() => onDrillDown({ accountId: acct.accountId, code: acct.code, name: acct.name, type: acct.type })}
              >
                <td className="px-6 py-3 font-mono text-sm text-slate-500">{acct.code}</td>
                <td className="px-6 py-3 font-medium text-brand hover:underline">{acct.name}</td>
                <td className="px-6 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${colors.bg} ${colors.text}`}>
                    {acct.type === "revenue" ? "Revenue" : acct.type.charAt(0).toUpperCase() + acct.type.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-3 text-right font-mono tabular-nums text-slate-700">
                  {acct.debitBalanceCents > 0 ? formatCents(acct.debitBalanceCents) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-6 py-3 text-right font-mono tabular-nums text-slate-700">
                  {acct.creditBalanceCents > 0 ? formatCents(acct.creditBalanceCents) : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-brand">
            <td className="px-6 py-3.5 font-bold text-white text-sm" colSpan={3}>
              TOTALS
            </td>
            <td className="px-6 py-3.5 text-right font-mono tabular-nums font-bold text-white text-sm">
              {formatCents(totals.totalDebitCents || 0)}
            </td>
            <td className="px-6 py-3.5 text-right font-mono tabular-nums font-bold text-white text-sm">
              {formatCents(totals.totalCreditCents || 0)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─── Balance Sheet ──────────────────────────────────────────────────

const BalanceSheetTable: React.FC<{ data: any; onDrillDown: (t: LedgerTarget) => void }> = ({ data, onDrillDown }) => {
  const { assets, liabilities, equity } = data;

  const SectionHeader: React.FC<{ title: string; type: string }> = ({ title, type }) => (
    <tr>
      <td colSpan={2} className={`px-6 py-3 font-bold text-slate-800 uppercase text-xs tracking-wider bg-slate-50 border-l-4 ${sectionAccent[type] || ""}`}>
        {title}
      </td>
    </tr>
  );

  const AccountRow: React.FC<{ acct: any; type: string }> = ({ acct, type }) => (
    <tr
      className="hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => onDrillDown({ accountId: acct.accountId, code: acct.code, name: acct.name, type })}
    >
      <td className="px-6 py-2.5 pl-8 text-slate-700">
        <span className="font-mono text-slate-400 text-xs mr-3">{acct.code}</span>
        <span className="font-medium text-brand hover:underline">{acct.name}</span>
      </td>
      <td className="px-6 py-2.5 text-right font-mono tabular-nums text-slate-700">
        {formatCents(acct.balanceCents)}
      </td>
    </tr>
  );

  const SectionTotal: React.FC<{ label: string; cents: number }> = ({ label, cents }) => (
    <tr className="border-t border-slate-200">
      <td className="px-6 py-2.5 pl-8 font-semibold text-slate-700">{label}</td>
      <td className="px-6 py-2.5 text-right font-mono tabular-nums font-semibold text-slate-800">
        {formatCents(cents)}
      </td>
    </tr>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand">
            <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Account</th>
            <th className="text-right px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {/* Assets */}
          <SectionHeader title="Assets" type="asset" />
          {(assets?.accounts || []).map((acct: any) => (
            <AccountRow key={acct.accountId} acct={acct} type="asset" />
          ))}
          <SectionTotal label="Total Assets" cents={assets?.totalCents || 0} />

          {/* Spacer */}
          <tr><td colSpan={2} className="py-1" /></tr>

          {/* Liabilities */}
          <SectionHeader title="Liabilities" type="liability" />
          {(liabilities?.accounts || []).map((acct: any) => (
            <AccountRow key={acct.accountId} acct={acct} type="liability" />
          ))}
          <SectionTotal label="Total Liabilities" cents={liabilities?.totalCents || 0} />

          {/* Spacer */}
          <tr><td colSpan={2} className="py-1" /></tr>

          {/* Equity */}
          <SectionHeader title="Equity" type="equity" />
          {(equity?.accounts || []).map((acct: any) => (
            <AccountRow key={acct.accountId} acct={acct} type="equity" />
          ))}
          {equity?.retainedEarningsCents !== 0 && (
            <tr>
              <td className="px-6 py-2.5 pl-8 text-slate-500 italic">
                <span className="font-mono text-slate-400 text-xs mr-3">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                Retained Earnings (Net Income)
              </td>
              <td className="px-6 py-2.5 text-right font-mono tabular-nums text-slate-500 italic">
                {formatCents(equity?.retainedEarningsCents || 0)}
              </td>
            </tr>
          )}
          <SectionTotal label="Total Equity" cents={equity?.totalCents || 0} />
        </tbody>
        <tfoot>
          <tr className="bg-brand">
            <td className="px-6 py-3.5 font-bold text-white text-sm">
              Total Liabilities + Equity
            </td>
            <td className="px-6 py-3.5 text-right font-mono tabular-nums font-bold text-white text-sm">
              {formatCents(data.totalLiabilitiesAndEquityCents || 0)}
            </td>
          </tr>
          {data.isBalanced === false && (
            <tr className="bg-red-50">
              <td colSpan={2} className="px-6 py-2.5 text-center">
                <span className="inline-flex items-center gap-2 text-red-700 text-xs font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Balance sheet is out of balance — Assets do not equal Liabilities + Equity
                </span>
              </td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
};

// ─── Income Statement ───────────────────────────────────────────────

const IncomeStatementTable: React.FC<{ data: any; onDrillDown: (t: LedgerTarget) => void }> = ({ data, onDrillDown }) => {
  const { revenue, expenses, netIncomeCents } = data;

  const SectionHeader: React.FC<{ title: string; type: string }> = ({ title, type }) => (
    <tr>
      <td colSpan={2} className={`px-6 py-3 font-bold text-slate-800 uppercase text-xs tracking-wider bg-slate-50 border-l-4 ${sectionAccent[type] || ""}`}>
        {title}
      </td>
    </tr>
  );

  const AccountRow: React.FC<{ acct: any; type: string }> = ({ acct, type }) => (
    <tr
      className="hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => onDrillDown({ accountId: acct.accountId, code: acct.code, name: acct.name, type })}
    >
      <td className="px-6 py-2.5 pl-8 text-slate-700">
        <span className="font-mono text-slate-400 text-xs mr-3">{acct.code}</span>
        <span className="font-medium text-brand hover:underline">{acct.name}</span>
      </td>
      <td className="px-6 py-2.5 text-right font-mono tabular-nums text-slate-700">
        {formatCents(acct.balanceCents)}
      </td>
    </tr>
  );

  const SectionTotal: React.FC<{ label: string; cents: number }> = ({ label, cents }) => (
    <tr className="border-t border-slate-200">
      <td className="px-6 py-2.5 pl-8 font-semibold text-slate-700">{label}</td>
      <td className="px-6 py-2.5 text-right font-mono tabular-nums font-semibold text-slate-800">
        {formatCents(cents)}
      </td>
    </tr>
  );

  const isProfit = (netIncomeCents || 0) >= 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand">
            <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Account</th>
            <th className="text-right px-6 py-3 text-xs font-semibold text-emerald-100 uppercase tracking-wider">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {/* Revenue */}
          <SectionHeader title="Revenue" type="revenue" />
          {(revenue?.accounts || []).map((acct: any) => (
            <AccountRow key={acct.accountId} acct={acct} type="revenue" />
          ))}
          <SectionTotal label="Total Revenue" cents={revenue?.totalCents || 0} />

          {/* Spacer */}
          <tr><td colSpan={2} className="py-1" /></tr>

          {/* Expenses */}
          <SectionHeader title="Expenses" type="expense" />
          {(expenses?.accounts || []).map((acct: any) => (
            <AccountRow key={acct.accountId} acct={acct} type="expense" />
          ))}
          <SectionTotal label="Total Expenses" cents={expenses?.totalCents || 0} />
        </tbody>
        <tfoot>
          {/* Net Income — prominent colored row */}
          <tr className={isProfit ? "bg-emerald-50" : "bg-red-50"}>
            <td className={`px-6 py-4 font-bold text-sm ${isProfit ? "text-emerald-800" : "text-red-800"}`}>
              <span className="flex items-center gap-2">
                {isProfit ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                NET INCOME
              </span>
            </td>
            <td className={`px-6 py-4 text-right font-mono tabular-nums font-bold text-lg ${isProfit ? "text-emerald-800" : "text-red-800"}`}>
              {formatCents(netIncomeCents || 0)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default FinancialStatementsTab;
